import dayjs from "dayjs";

// material ui
import { Grid, Typography, TextField, createFilterOptions } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

// project import
import { useTranslation } from "utils/locales/utilityFunctions";
import { FirestoreInventoryItemProps } from "types/auth";
import { ActivityOption } from "components/estatesAndMapSelecter/getActivityOptions";
import AutoCompleteGrouped from "components/input/autoCompleteGrouped";
import AutoCompleteCreatable from "components/input/autoCompleteCreatable";
import { ContactType } from "components/shareData/shareDialog";
import { validateEmail } from "utils/textModification";

interface RawWoodInputInformationOriginProps {
  variant: FirestoreInventoryItemProps["inventoryType"];
  disableInputs: boolean;
  originsOptions: ActivityOption[];
  origins?: ActivityOption[];
  responsibleOptions: ContactType[];
  responsible?: ContactType[];
  date: Date | null;
  handleChangeOrigins: (v: any) => void;
  handleChangeResponsible: (v: any) => void;
  handleChangeDate: (date: Date | null) => void;
}

const filterContacts = createFilterOptions<ContactType>();

const RawWoodInputInformationOrigin = ({
  variant,
  disableInputs,
  originsOptions,
  origins,
  responsibleOptions,
  responsible,
  date,
  handleChangeOrigins,
  handleChangeResponsible,
  handleChangeDate,
}: RawWoodInputInformationOriginProps) => {
  // --- Hooks --- //
  const { translate } = useTranslation();

  // --- Functions --- //
  const handleContactFilter = (options: any, params: any) => {
    const filtered = filterContacts(options, params);
    const { inputValue } = params;
    // Check for existing option
    const isExisting = options.some((option: ContactType) => inputValue === option.email);
    // Check input to be email address
    const validEmail = validateEmail(inputValue);
    if (validEmail && !isExisting) {
      filtered.push({
        inputValue,
        email: inputValue,
        label: inputValue,
        name: inputValue,
        personType: "contact",
      });
    }
    return filtered;
  };

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="h5">
          {translate("input-dialog-raw-wood-tab-information-origin")}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={12 / 3}>
        <AutoCompleteGrouped
          title={translate("input-dialog-raw-wood-tab-information-origin")}
          label={translate("input-dialog-raw-wood-tab-information-origin")}
          showTitle={false}
          showLabel={true}
          data={originsOptions}
          groupByField={"projectName"}
          renderOptionField={"activityName"}
          renderTagField={"label"}
          noOptionsText={translate("no-results")}
          changeHandler={(_, v) => handleChangeOrigins(v)}
          value={origins}
          multiple={true}
          noWrap={true}
          disabled={disableInputs}
        />
      </Grid>

      <Grid item xs={12} sm={12 / 3}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjs.locale("da")}>
          <DatePicker
            label={translate("input-dialog-raw-wood-tab-information-origin-date")}
            value={date}
            onChange={handleChangeDate}
            disabled={disableInputs}
            renderInput={(params) => (
              <TextField
                fullWidth
                color="secondary"
                {...params}
                error={false}
                sx={{
                  "& .MuiFormLabel-root": { fontSize: 14.5 },
                  "& label": {
                    marginTop: date !== null ? "0px" : "-5px",
                    "&.Mui-focused": {
                      marginTop: "0px",
                    },
                    color: "secondary.600",
                  },
                }}
              />
            )}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12} sm={12 / 3}>
        <AutoCompleteCreatable
          title={translate("input-dialog-raw-wood-tab-information-origin-registered-of")}
          label={translate("input-dialog-raw-wood-tab-information-origin-registered-of")}
          showLabel={true}
          showTitle={false}
          groupByField="personTypeLabelText"
          renderOptionField="name"
          renderTagField="name"
          data={responsibleOptions}
          value={responsible}
          changeHandler={(_, v) => handleChangeResponsible(v)}
          filterHandler={handleContactFilter}
          multiple={true}
          filterSelectedOptions={true}
          noWrap={true}
          disabled={disableInputs}
        />
      </Grid>
    </>
  );
};

export default RawWoodInputInformationOrigin;
